import * as Tone from 'tone';
import type { SimEvent } from '../sim/types.js';

/**
 * Synthesized audio (Tone.js). A pure consumer of sim events — no game logic
 * here. Must be started from a user gesture (the start-gate tap) so the iOS
 * Web-Audio context can resume.
 */
export interface GameAudio {
  /** Resume the audio context and spin up the beat/ambient. Call on a gesture. */
  start(): Promise<void>;
  /** React to a frame's worth of sim events. */
  handle(events: SimEvent[]): void;
  /** Electric Ball Room: continuous coil hum (∝ voltage) + arc crackle. */
  setElectric(voltage: number, attracting: boolean): void;
  /** A big explosion boom. */
  boom(): void;
  /** The Cat room: a reverberant footstep (high ceiling). */
  catStep(): void;
  /** The Cat room: a synthesized meow (you observed the cat). */
  meow(): void;
  /** A dull impact (e.g. clipped by the pendulum). */
  hit(): void;
  /** Continuous swing "whoosh" (level 0..1) — e.g. the pendulum sweeping. */
  setWhoosh(level: number): void;
  dispose(): void;
}

export function createGameAudio(): GameAudio {
  let started = false;
  let initializing = false;

  // Lazily-created nodes (only after start(), inside the resumed context).
  let master: Tone.Gain;
  let kick: Tone.MembraneSynth;
  let beat: Tone.Loop;
  let drone: Tone.Oscillator;
  let blip: Tone.Synth;
  let thud: Tone.MembraneSynth;
  let buzz: Tone.NoiseSynth;
  let chime: Tone.Synth;
  let stepNoise: Tone.NoiseSynth;
  let stepFilter: Tone.Filter;
  let coilOsc: Tone.Oscillator;
  let coilGain: Tone.Gain;
  let crackle: Tone.Noise;
  let crackleGain: Tone.Gain;
  let reverb: Tone.Reverb;
  let catStepNoise: Tone.NoiseSynth;
  let meowSynth: Tone.MonoSynth;
  let whoosh: Tone.Noise;
  let whooshGain: Tone.Gain;
  let whooshFilter: Tone.Filter;

  return {
    async start() {
      // Only flag `started` once every node exists, so the per-frame methods
      // (which guard on `started`) never touch a node mid-construction.
      if (started || initializing) return;
      initializing = true;
      await Tone.start();

      master = new Tone.Gain(0.7).toDestination();

      // Low-frequency pulse — a quiet heartbeat under everything.
      kick = new Tone.MembraneSynth({ volume: -10, octaves: 4, pitchDecay: 0.05 }).connect(master);
      const transport = Tone.getTransport();
      transport.bpm.value = 70;
      beat = new Tone.Loop((time) => {
        kick.triggerAttackRelease('C1', '8n', time);
      }, '2n').start(0);
      transport.start();

      // Subtle ambient drone.
      drone = new Tone.Oscillator({ frequency: 55, type: 'sine', volume: -28 }).connect(master);
      drone.start();

      // SFX voices.
      blip = new Tone.Synth({ oscillator: { type: 'triangle' }, volume: -14 }).connect(master);
      thud = new Tone.MembraneSynth({ volume: -14, pitchDecay: 0.08 }).connect(master);
      buzz = new Tone.NoiseSynth({
        noise: { type: 'pink' },
        envelope: { attack: 0.005, decay: 0.18, sustain: 0 },
        volume: -16,
      }).connect(master);
      chime = new Tone.Synth({ oscillator: { type: 'sine' }, volume: -16 }).connect(master);

      // Footsteps: brown-noise burst through a moving filter.
      stepFilter = new Tone.Filter(420, 'lowpass').connect(master);
      stepFilter.Q.value = 2;
      stepNoise = new Tone.NoiseSynth({
        noise: { type: 'brown' },
        envelope: { attack: 0.004, decay: 0.07, sustain: 0 },
        volume: -18,
      }).connect(stepFilter);

      // Electric Ball Room: a coil hum whose pitch/volume rise with voltage,
      // and a high crackle for the arc.
      coilGain = new Tone.Gain(0).connect(master);
      const coilFilter = new Tone.Filter(900, 'lowpass').connect(coilGain);
      coilOsc = new Tone.Oscillator({ frequency: 55, type: 'sawtooth', volume: -4 }).connect(
        coilFilter,
      );
      coilOsc.start();
      crackleGain = new Tone.Gain(0).connect(master);
      const crackleFilter = new Tone.Filter(2600, 'highpass').connect(crackleGain);
      crackle = new Tone.Noise({ type: 'white', volume: -10 }).connect(crackleFilter);
      crackle.start();

      // (cat-room nodes below) — `started` is set at the very end of start().

      // The Cat room: a big reverberant space + a meow. A wet reverb on the
      // footsteps sells the high ceiling.
      reverb = new Tone.Reverb({ decay: 3.8, preDelay: 0.02, wet: 0.85 }).connect(master);
      catStepNoise = new Tone.NoiseSynth({
        noise: { type: 'brown' },
        envelope: { attack: 0.004, decay: 0.22, sustain: 0 },
        volume: -8,
      }).connect(reverb);
      meowSynth = new Tone.MonoSynth({
        oscillator: { type: 'sawtooth' },
        filter: { Q: 4, type: 'lowpass' },
        filterEnvelope: {
          attack: 0.04,
          decay: 0.2,
          sustain: 0.5,
          release: 0.3,
          baseFrequency: 700,
          octaves: 2,
        },
        envelope: { attack: 0.03, decay: 0.1, sustain: 0.7, release: 0.25 },
        volume: -6,
      }).connect(reverb);

      // Swing whoosh: pink noise through a moving band-pass.
      whooshGain = new Tone.Gain(0).connect(master);
      whooshFilter = new Tone.Filter(500, 'bandpass').connect(whooshGain);
      whooshFilter.Q.value = 1.2;
      whoosh = new Tone.Noise({ type: 'pink', volume: -2 }).connect(whooshFilter);
      whoosh.start();

      started = true;
      initializing = false;
    },

    setElectric(voltage, attracting) {
      if (!started) return;
      coilGain.gain.rampTo(voltage * 0.22, 0.06);
      coilOsc.frequency.rampTo(45 + voltage * 130, 0.06);
      crackleGain.gain.rampTo(attracting ? 0.16 : 0, 0.08);
    },

    boom() {
      if (!started) return;
      thud.triggerAttackRelease('C0', '1n');
      buzz.triggerAttackRelease('2n');
      chime.triggerAttackRelease('C3', '2n', Tone.now() + 0.04);
    },

    catStep() {
      if (!started) return;
      catStepNoise.triggerAttackRelease('8n');
    },

    meow() {
      if (!started) return;
      const t = Tone.now();
      meowSynth.triggerAttack('E4', t);
      meowSynth.frequency.setValueAtTime(330, t);
      meowSynth.frequency.linearRampToValueAtTime(560, t + 0.16);
      meowSynth.frequency.linearRampToValueAtTime(300, t + 0.5);
      meowSynth.triggerRelease(t + 0.55);
    },

    hit() {
      if (!started) return;
      thud.triggerAttackRelease('A1', '8n');
      buzz.triggerAttackRelease('16n');
    },

    setWhoosh(level) {
      if (!started) return;
      const l = Math.max(0, Math.min(1, level));
      whooshGain.gain.rampTo(l * 0.2, 0.05);
      whooshFilter.frequency.rampTo(300 + l * 900, 0.05);
    },

    handle(events) {
      if (!started) return;
      for (const e of events) {
        switch (e.type) {
          case 'footstep':
            stepFilter.frequency.value = 300 + Math.random() * 420;
            stepNoise.triggerAttackRelease('16n');
            break;
          case 'jump':
            blip.triggerAttackRelease('A4', '16n');
            break;
          case 'land':
            thud.triggerAttackRelease('C2', '16n');
            break;
          case 'bump':
            thud.triggerAttackRelease('G1', '16n');
            buzz.triggerAttackRelease('16n');
            break;
          case 'board-enter':
            chime.triggerAttackRelease('E5', '8n');
            break;
          case 'handle-reject':
            buzz.triggerAttackRelease('16n');
            break;
          case 'handle-operate':
            blip.triggerAttackRelease('C4', '8n');
            break;
          case 'beam-on':
            chime.triggerAttackRelease('C6', '2n');
            break;
          case 'complete':
            chime.triggerAttackRelease('C5', '1n');
            blip.triggerAttackRelease('G5', '2n', Tone.now() + 0.1);
            break;
          default:
            break;
        }
      }
    },

    dispose() {
      if (!started) return;
      beat.dispose();
      kick.dispose();
      drone.dispose();
      blip.dispose();
      thud.dispose();
      buzz.dispose();
      chime.dispose();
      stepNoise.dispose();
      stepFilter.dispose();
      coilOsc.dispose();
      coilGain.dispose();
      crackle.dispose();
      crackleGain.dispose();
      reverb.dispose();
      catStepNoise.dispose();
      meowSynth.dispose();
      whoosh.dispose();
      whooshGain.dispose();
      whooshFilter.dispose();
      master.dispose();
    },
  };
}
